import type { ChangeEvent, FC } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";

import { Container, ContentContainer } from "@src/core/styles";
import { useSeo } from "@src/core/hooks";
import { APP_ROUTES } from "@src/core/constants";
import {
  ActionButton,
  ArticleBody,
  Chip,
  InfoGrid,
  InfoItem,
  MonoArea,
  Panel,
  PanelActions,
  PanelBody,
  PanelHeader,
  PanelTitle,
  Section,
  SectionTitle,
  SmallButton,
  Tabs,
  ToolContainer,
  ToolIntro,
  ToolSubtitle,
  ToolTitle,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
} from "@src/pages/dev-tools-shared";

type Mode = "encode" | "decode";

const toBase64 = (str: string, urlSafe: boolean): string => {
  const encoder = new TextEncoder();
  const bytes = encoder.encode(str);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base = btoa(binary);
  if (!urlSafe) return base;
  return base.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const fromBase64 = (input: string, urlSafe: boolean): string => {
  let normalised = input.trim();
  if (urlSafe) {
    normalised = normalised.replace(/-/g, "+").replace(/_/g, "/");
    while (normalised.length % 4) normalised += "=";
  }
  const binary = atob(normalised);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const decoder = new TextDecoder("utf-8", { fatal: true });
  return decoder.decode(bytes);
};

export const Base64Page: FC = () => {
  const [mode, setMode] = useState<Mode>("encode");
  const [urlSafe, setUrlSafe] = useState<boolean>(false);
  const [input, setInput] = useState<string>("");
  const [output, setOutput] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [fileInfo, setFileInfo] = useState<{ name: string; size: number } | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!input) {
      setOutput("");
      setError("");
      return;
    }
    try {
      if (mode === "encode") {
        setOutput(toBase64(input, urlSafe));
        setError("");
      } else {
        setOutput(fromBase64(input, urlSafe));
        setError("");
      }
    } catch (e) {
      setOutput("");
      setError(
        mode === "decode"
          ? "Строка не является корректным Base64."
          : "Не удалось выполнить преобразование."
      );
    }
  }, [input, mode, urlSafe]);

  const handleSwap = useCallback(() => {
    setMode((prev) => (prev === "encode" ? "decode" : "encode"));
    setInput(output);
  }, [output]);

  const handleCopy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      enqueueSnackbar("Скопировано в буфер обмена", {
        variant: "success",
        preventDuplicate: true,
      });
    } catch {
      enqueueSnackbar("Не удалось скопировать", { variant: "error" });
    }
  };

  const handleClear = () => {
    setInput("");
    setOutput("");
    setError("");
    setFileInfo(null);
  };

  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (result instanceof ArrayBuffer) {
        const bytes = new Uint8Array(result);
        let binary = "";
        for (let i = 0; i < bytes.byteLength; i += 1) {
          binary += String.fromCharCode(bytes[i]);
        }
        const encoded = btoa(binary);
        const withPrefix = `data:${file.type || "application/octet-stream"};base64,${encoded}`;
        setMode("encode");
        setInput(file.name);
        setOutput(urlSafe ? encoded.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "") : withPrefix);
        setFileInfo({ name: file.name, size: file.size });
        setError("");
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  const stats = useMemo(() => {
    if (!output) return null;
    return {
      inputChars: input.length,
      outputChars: output.length,
      outputBytes: new Blob([output]).size,
    };
  }, [input, output]);

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Base64 Encoder / Decoder онлайн",
      applicationCategory: "DeveloperApplication",
      operatingSystem: "All",
      offers: { "@type": "Offer", price: "0", priceCurrency: "RUB" },
      description:
        "Кодирование и декодирование Base64 онлайн: обычный и URL-safe режим, поддержка UTF-8 и файлов. Все вычисления происходят в браузере.",
      url: APP_ROUTES.BASE64,
    }),
    []
  );

  useSeo({
    title: "Base64 — онлайн кодирование и декодирование | URL-safe, UTF-8",
    description:
      "Онлайн Base64 кодировщик и декодер: стандартный и URL-safe формат, поддержка UTF‑8, кодирование файлов в data URI. Работает офлайн в браузере.",
    canonical: APP_ROUTES.BASE64,
    jsonLd,
  });

  return (
    <Container>
      <ContentContainer>
        <ToolContainer>
          <ToolIntro>
            <ToolTitle>Base64 Encoder / Decoder</ToolTitle>
            <ToolSubtitle>
              Закодируйте текст или файл в Base64 или раскодируйте строку
              обратно. Поддерживается UTF‑8 и URL‑safe вариант (RFC 4648 §5).
              Данные не покидают ваш браузер.
            </ToolSubtitle>
          </ToolIntro>

          <Tabs>
            <Chip
              $active={mode === "encode"}
              onClick={() => setMode("encode")}
              type="button"
            >
              Кодировать → Base64
            </Chip>
            <Chip
              $active={mode === "decode"}
              onClick={() => setMode("decode")}
              type="button"
            >
              Декодировать Base64 →
            </Chip>
          </Tabs>

          <Toolbar>
            <ToolbarLeft>
              <ActionButton variant="outlined" onClick={handleSwap}>
                ⇅ Поменять местами
              </ActionButton>
              <ActionButton variant="outlined" onClick={handleClear}>
                Очистить
              </ActionButton>
            </ToolbarLeft>
            <ToolbarRight>
              <Chip
                $active={urlSafe}
                onClick={() => setUrlSafe((v) => !v)}
                type="button"
                title="URL-safe: +/= заменяются на -_"
              >
                URL-safe
              </Chip>
            </ToolbarRight>
          </Toolbar>

          <Panel>
            <PanelHeader>
              <PanelTitle>
                {mode === "encode" ? "Исходный текст" : "Base64"}
              </PanelTitle>
              <PanelActions>
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleFile}
                />
                {mode === "encode" && (
                  <SmallButton
                    variant="outlined"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Загрузить файл
                  </SmallButton>
                )}
              </PanelActions>
            </PanelHeader>
            <PanelBody>
              <MonoArea
                multiline
                minRows={8}
                maxRows={20}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={
                  mode === "encode"
                    ? "Введите текст или загрузите файл…"
                    : "Вставьте Base64 строку…"
                }
              />
            </PanelBody>
          </Panel>

          <Panel>
            <PanelHeader>
              <PanelTitle>Результат</PanelTitle>
              <PanelActions>
                <SmallButton
                  variant="outlined"
                  onClick={handleCopy}
                  disabled={!output}
                >
                  Копировать
                </SmallButton>
              </PanelActions>
            </PanelHeader>
            <PanelBody>
              <MonoArea
                multiline
                minRows={8}
                maxRows={20}
                value={output}
                InputProps={{ readOnly: true }}
                placeholder="Результат появится здесь автоматически…"
                error={!!error}
                helperText={error || undefined}
              />
            </PanelBody>
          </Panel>

          {(stats || fileInfo) && (
            <InfoGrid>
              {fileInfo && (
                <>
                  <InfoItem>
                    <strong>Файл:</strong> {fileInfo.name}
                  </InfoItem>
                  <InfoItem>
                    <strong>Размер:</strong> {fileInfo.size} байт
                  </InfoItem>
                </>
              )}
              {stats && (
                <>
                  <InfoItem>
                    <strong>Исходная длина:</strong> {stats.inputChars}
                  </InfoItem>
                  <InfoItem>
                    <strong>Длина результата:</strong> {stats.outputChars}
                  </InfoItem>
                  <InfoItem>
                    <strong>Размер:</strong> {stats.outputBytes} байт
                  </InfoItem>
                </>
              )}
            </InfoGrid>
          )}

          <Section>
            <SectionTitle>Что такое Base64 и зачем он нужен</SectionTitle>
            <ArticleBody>
              <p>
                <strong>Base64</strong> — это схема кодирования бинарных данных
                в ASCII‑строку из 64 символов (<code>A‑Z</code>, <code>a‑z</code>
                , <code>0‑9</code>, <code>+</code>, <code>/</code>, выравнивание
                <code>=</code>). Используется там, где нужно передать бинарные
                байты через текстовый канал: email (MIME), JSON, HTTP‑заголовки
                (<code>Authorization: Basic …</code>), Data URI для картинок и
                шрифтов.
              </p>
              <p>
                В <strong>URL‑safe</strong> варианте (RFC 4648 §5) символы{" "}
                <code>+</code> и <code>/</code> заменяются на <code>-</code> и
                <code>_</code>, а padding <code>=</code> обычно убирается. Это
                нужно, чтобы строка безопасно помещалась в URL и путь файла.
                Именно так кодируются header и payload в{" "}
                <a href={APP_ROUTES.JWT_DEBUGGER}>JWT‑токенах</a>.
              </p>
              <h3>Как выглядит Base64 для разных данных</h3>
              <ul>
                <li>
                  <code>hello</code> → <code>aGVsbG8=</code>
                </li>
                <li>
                  <code>{`{"ok": true}`}</code> →{" "}
                  <code>eyJvayI6IHRydWV9</code>
                </li>
                <li>
                  Картинка PNG → <code>iVBORw0KGgoAAAANSUhEUgAA…</code>
                </li>
              </ul>
              <p>
                Base64 <strong>не шифрование</strong>: любой может декодировать
                строку обратно. Это только кодирование формата.
              </p>
            </ArticleBody>
          </Section>
        </ToolContainer>
      </ContentContainer>
    </Container>
  );
};
